import { cn } from '@/lib/utils';
import { useRef, type InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  /**
   * Select the field's contents on focus. Handy for number fields that default
   * to `0` (or any prefilled value) so the first keystroke replaces the value
   * instead of appending to it (`0` → `05`). Works for both keyboard (tab) and
   * mouse: on the focusing click we suppress the caret-placement mouseup so the
   * select-all survives, then restore normal caret positioning for later clicks.
   */
  selectOnFocus?: boolean;
}

export function Input({ className, error, selectOnFocus, onFocus, onMouseDown, onMouseUp, ...props }: InputProps) {
  // True between the mousedown that moves focus into the field and its mouseup,
  // so we only cancel the caret placement for that first focusing click.
  const focusingClick = useRef(false);

  return (
    <input
      className={cn(
        'h-9 w-full rounded-lg border px-3 text-sm outline-none transition-colors',
        'border-input focus:border-primary focus:ring-1 focus:ring-primary',
        error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
        className,
      )}
      onFocus={(e) => {
        if (selectOnFocus) e.currentTarget.select();
        onFocus?.(e);
      }}
      onMouseDown={(e) => {
        if (selectOnFocus) focusingClick.current = document.activeElement !== e.currentTarget;
        onMouseDown?.(e);
      }}
      onMouseUp={(e) => {
        // On the click that just focused the field, keep the select-all from
        // onFocus by preventing the default caret placement. Subsequent clicks
        // (already focused) fall through so the user can position the caret.
        if (selectOnFocus && focusingClick.current) {
          e.preventDefault();
          focusingClick.current = false;
        }
        onMouseUp?.(e);
      }}
      {...props}
    />
  );
}
